import React, { useState } from "react";
import PropTypes from "prop-types";
import Grid from '@mui/material/Grid';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import districtImg from '../../../licking_heights.png';
import districtDefaultImg from '../../../IB_Simbol_DB_RGB.png';
import PlaceIcon from '@mui/icons-material/Place';
import ReactCardFlip from "react-card-flip";
import Button from "@mui/material/Button";
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import { NULL_DISPLAY_TEXT, nullText } from '../../atoms/NullDisplay/NullDisplay';
import Print from '@mui/icons-material/Print';
import Box from "@mui/material/Box";
import {printSection} from "../../../utils/helpsers";
import SalaryTable from "./SalaryTable";

// Component Styles.
import {DistrictCardStyles as styles} from './DistrcitCard.styles'

/**
 * Card with the data of the referenced district.
 * @param {object} data - Object information about the reference district.
 * @returns {JSX.Element}
 */
const DistrictCard = ({ data }) => {
  // Split District name and county.
  let districtFullName = (data?.district_name)?.split(',');
  let districtName = districtFullName?.[0];
  let districtCounty = districtFullName?.[1];

  const formatAddress = (address) => {
    const acronyms = ["sw", "rd", "way", "ave", "st", "blvd", "dr", "avenue", "pike", "w", "n", "e", "o"];
    let city = "";
    let addressArray = address?.split(',');
    let partDirection1 = addressArray?.[0]?.split(' ').reverse();
    let index = partDirection1?.findIndex((value) => acronyms?.includes(value.toLowerCase()));
    if(index === -1) {
      index = partDirection1?.findIndex((value) => !isNaN(value))
    }
    city = partDirection1?.slice(0, index).reverse().join(' ');
    let direction1 = partDirection1?.slice(index).reverse().join(' ');
    let direction2 = `${city}, ${addressArray?.[1]}`;

    return [direction1, direction2];
  }

  // Split Address
  let fullAddress = formatAddress(data?.physical_address?.value);

  const [flipCard, setFlipCard] = useState(false);
  let static_data = Object.entries(data?.static_data).sort((a, b) => parseFloat(a[1]?.var_order) - parseFloat(b[1]?.var_order));

  let table_data = Object.entries(data?.table_data).sort((a, b) => parseFloat(a[1]?.var_order) - parseFloat(b[1]?.var_order));

  /**
   *
   * @param arr
   *   The array object to be validated.
   * @returns [{}]
   */
  const validated_data = arr => {
    let keysToValidate = ['value', 'state', 'rank_in_state']
    let filtered_array = [];

    arr.forEach(el => {
      if(el[1]) {
        const displayText = el[1].null_reason ? nullText(el[1].null_reason) : NULL_DISPLAY_TEXT;
        keysToValidate.forEach(key => {
          if (el[1][key] === null || el[1][key] === '' || el[1][key] === 'N/A') {
            el[1][key] = displayText;
          }
        })
        filtered_array.push(el)
      } else {
        filtered_array.push( el = {...el, 1: {'label': el[0], 'value': NULL_DISPLAY_TEXT}});
      }
    });

    return filtered_array;
  };

  let staticRender = (entry, key) => {
    if (!['federal_revenue_per_pupil', 'state_revenue_per_pupil', 'local_revenue_per_pupil', 'other_non_tax_revenue_per_pupil'].includes(entry[0])) {
      return (
        <div key={key}>
          <div className="DistrictCard__data">
            <div className="label">{entry[1].label}</div>
            <div className="value">{entry[1].value}</div>
          </div>
          {
            data && entry[0] === 'revenue_per_pupil' &&
            <>
              <div className="DistrictCard__revenue">
                <span className="label">Federal: </span> <span className="district-value">{data?.static_data.federal_revenue_per_pupil.value}</span>
              </div> <span>+</span>
              <div className="DistrictCard__revenue">
                <span className="label">State: </span> <span className="district-value">{data?.static_data.state_revenue_per_pupil.value}</span>
              </div> <span>+</span>
              <div className="DistrictCard__revenue">
                <span className="label">Local: </span> <span className="district-value">{data?.static_data.local_revenue_per_pupil.value}</span>
              </div> <span>+</span>
              <div className="DistrictCard__revenue">
                <span className="label">Other: </span> <span className="district-value">{data?.static_data.other_non_tax_revenue_per_pupil.value}</span>
              </div>
            </>
          }
        </div>
      )
    }
  }

  return (
    <Box className="DistrictCard" sx={styles}>
      <ReactCardFlip isFlipped={flipCard} flipDirection="horizontal">
        <div className="DistrictCard__wrapper">
          <div className="DistrictCard__header">
            <div className="DistrictCard__definition">
              <h2>{districtName}</h2>
              <div className="DistrictCard__address">
                <div className="DistrictCard__icon">
                  <PlaceIcon />
                </div>
                <div className="DistrictCard__address-info">
                  <div>{fullAddress[0]}</div>
                  <div>{fullAddress[1]}</div>
                </div>
              </div>
              <div className="DistrictCard__info">
                <span className="label">IRN: </span><span className="district-value">{data?.irn}</span>
              </div>
              <div className="DistrictCard__info">
                <span className="label">Career-Technical School: </span>
                <a href={`https://reportcard.education.ohio.gov/ctpd/print/${data?.career_technical_school?.link}`} target="_blank" rel="noopener noreferrer">
                  {data?.career_technical_school?.value}
                </a>
              </div>
              <div className="DistrictCard__info">
                <span className="label">County: </span><span className={styles.districtValue}>{districtCounty}</span>
              </div>
            </div>
            <div className="DistrictCard__image">
              {
                data?.district_name === 'Licking Heights Local SD, Licking'
                  ? <img src={districtImg} alt="Licking Heights Hornets" />
                  : <img src={districtDefaultImg} alt="insight board" />
              }
            </div>
          </div>

          <div className="DistrictCard__content">
            <Grid container spacing={0}>
              <Grid item xs={12}>
                {
                  data?.salary_data &&
                  <SalaryTable salaryData={data} staticData={static_data}/>
                }
                {
                  // Return validate data only in insight board.
                  !(data?.table_data?.base_salary_ba || data?.table_data?.base_salary_ma) &&
                  validated_data(static_data).map((entry, key) => {
                    return staticRender(entry, key);
                  })
                }
              </Grid>
            </Grid>
          </div>
        </div>

        <div className="DistrictCard">
          <div className="DistrictCard__content">
            <TableContainer className="FlipTableStyles">
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left"><span>Category</span></TableCell>
                    <TableCell align="center"><span>District</span></TableCell>
                    <TableCell align="center"><span>State Average</span></TableCell>
                    <TableCell align="center"><span>State Rank</span></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    validated_data(table_data).map(entry => {
                      return (
                        <TableRow
                          key={entry[1].label}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">{entry[1].label}</TableCell>
                          <TableCell align="right">{entry[1].value}</TableCell>
                          <TableCell align="right">{entry[1].state}</TableCell>
                          <TableCell align="right">{entry[1].rank_in_state}</TableCell>
                        </TableRow>
                      )
                    })
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </ReactCardFlip>
      <Button variant="outline" id="printCard" className="DistrictCard__print" onClick={ ()=> printSection({
        section: '.react-card-flip',
        title: `${data.district_name} District Information`,
        printStyles: `#printCard,
                div[class*="DistrictCard__icon"],
                div[class*="DistrictCard__image"]{display: none}
                .react-card-front, .react-card-back{all: initial!important;}
                div[class*="DistrictCard__header"],
                div[class*="DistrictCard__content"]{border-bottom: 1px solid #cbcbcb;margin-bottom: 10px; padding-bottom: 10px;}
                div[class*="DistrictCard__data"]{display: flex; margin-bottom: 10px;}
                div[class*="label"]{min-width: 400px;}
                div[class*="DistrictCard__revenue"]{display: inline-block;margin-bottom: 10px;}
                div[class*="label"]{font-weight: bold}
                table th{text-align: left;}
                table tr th,
                table tr td{padding: 6px 0;}`})}>
        print card
        <Print/>
      </Button>
      <div className="DistrictCard__actions">
        <Button variant="contained" className="FlipButton" onClick={() => setFlipCard(!flipCard)}>
          <FlipCameraAndroidIcon />
        </Button>
      </div>
    </Box>
  );
};

DistrictCard.propTypes = {
  data: PropTypes.object.isRequired,
}

export default React.memo(DistrictCard);
